
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const formSchema = z.object({
  requireMfa: z.boolean().default(false),
  mfaMethod: z.enum(["email", "authenticator", "sms"]).default("email"),
  passwordPolicy: z.enum(["standard", "high", "extreme"]).default("standard"),
  sessionTimeout: z.enum(["15m", "30m", "1h", "8h", "24h"]).default("1h"),
});

type FormValues = z.infer<typeof formSchema>;

const SystemSettings = () => {
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      requireMfa: false,
      mfaMethod: "email",
      passwordPolicy: "standard",
      sessionTimeout: "1h",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setIsSaving(true);
    
    // Simulate saving to the server
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log("Security settings updated:", values);
    
    toast({
      title: "Settings saved",
      description: "Security settings have been updated successfully",
    });
    
    setIsSaving(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>System Settings</CardTitle>
        <CardDescription>
          Configure system-wide settings and security policies
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Authentication Settings</h3>
              
              <FormField
                control={form.control}
                name="requireMfa"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Multi-factor Authentication
                      </FormLabel>
                      <FormDescription>
                        Require multi-factor authentication for all users
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              {form.watch("requireMfa") && (
                <FormField
                  control={form.control}
                  name="mfaMethod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>MFA Method</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select MFA method" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="email">Email code</SelectItem>
                          <SelectItem value="authenticator">Authenticator app</SelectItem>
                          <SelectItem value="sms">SMS</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Choose how users will verify their identity
                      </FormDescription>
                    </FormItem>
                  )}
                />
              )}
              
              <Separator />
              
              <FormField
                control={form.control}
                name="passwordPolicy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password Policy</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select password policy" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="high">High Security</SelectItem>
                        <SelectItem value="extreme">Extreme Security</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Configure password strength requirements
                    </FormDescription>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="sessionTimeout"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Session Timeout</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select session timeout" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="15m">15 minutes</SelectItem>
                        <SelectItem value="30m">30 minutes</SelectItem>
                        <SelectItem value="1h">1 hour</SelectItem>
                        <SelectItem value="8h">8 hours</SelectItem>
                        <SelectItem value="24h">24 hours</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      How long until inactive users are logged out
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
            
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Settings"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default SystemSettings;
